import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarDays, MapPin, Clock, Ticket } from "lucide-react";
import api, { img, apiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { SectionTitle } from "@/components/Bits";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

export default function Workshops() {
  const { t, L } = useLang();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/workshops").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/workshops/${active.id}/signup`, form);
      toast.success(data.confirmation || L("报名成功", "Registered"));
      setActive(null);
      setForm({ name: "", phone: "", email: "" });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
      <SectionTitle tag="WORKSHOPS · 工作坊" title={t("workshops_title")} />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {items.map((w, i) => (
          <motion.div
            key={w.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
            className="bg-white fk-border-thick rounded-2xl overflow-hidden fk-lift" data-testid={`workshop-card-${w.id}`}
          >
            <img src={img(w.image)} alt={w.title_en} className="w-full h-48 object-cover border-b-2 border-black" />
            <div className="p-6">
              <h3 className="font-display text-2xl">{L(w.title_zh, w.title_en)}</h3>
              <p className="mt-2 text-sm text-black/70 line-clamp-2">{L(w.description_zh, w.description_en)}</p>
              <div className="mt-4 space-y-1.5 font-mono-fk text-xs text-black/70">
                <div className="flex items-center gap-2"><CalendarDays size={14} /> {w.date} {w.time && `· ${w.time}`}</div>
                <div className="flex items-center gap-2"><MapPin size={14} /> {L(w.location_zh, w.location_en)}</div>
                {w.price && <div className="flex items-center gap-2"><Ticket size={14} /> {w.price}</div>}
              </div>
              <button
                onClick={() => setActive(w)}
                data-testid={`workshop-signup-${w.id}`}
                className="mt-5 w-full fk-border rounded-full px-4 py-2.5 font-semibold bg-[#FF5757] text-white fk-lift"
              >
                {t("sign_up")}
              </button>
            </div>
          </motion.div>
        ))}
      </div>
      {items.length === 0 && <p className="text-black/50 font-mono-fk">暂无工作坊 / No workshops.</p>}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="fk-border-thick rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{active && L(active.title_zh, active.title_en)}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium">{t("name")}</label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ws-name" className="fk-border rounded-lg mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium">{t("phone")}</label>
              <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="ws-phone" className="fk-border rounded-lg mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium">{t("email")}</label>
              <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ws-email" className="fk-border rounded-lg mt-1" />
            </div>
            <button disabled={submitting} data-testid="ws-submit" className="w-full fk-border rounded-full px-5 py-3 font-semibold bg-[#FFD93D] fk-lift disabled:opacity-50">
              <Clock size={15} className="inline mr-1" /> {submitting ? "…" : t("submit")}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
